import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({
  theme: "light",
  themePreference: "system",
  setThemePreference: () => {},
  toggleTheme: () => {},
});

const THEME_STORAGE_KEY = "bainv-theme";
const THEME_PREFERENCES = ["light", "white", "dark", "system"];

const getStoredPreference = () => {
  if (typeof window === "undefined") {
    return "system";
  }
  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return THEME_PREFERENCES.includes(storedTheme) ? storedTheme : "system";
};

const getSystemTheme = () => {
  if (typeof window === "undefined") {
    return "light";
  }
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const THEME_COLORS = {
  light: "#fff8f0",
  white: "#ffffff",
  dark: "#1c1c1c",
};

const applyThemeToDom = (theme) => {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme === "dark" ? "dark" : "light";
  root.classList.toggle("dark", theme === "dark");

  if (document.body) {
    document.body.classList.remove("theme-light", "theme-white", "theme-dark");
    document.body.classList.add(`theme-${theme}`);
  }

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute(
      "content",
      THEME_COLORS[theme] || THEME_COLORS.light
    );
  }
};

export function ThemeProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState(getStoredPreference);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const resolvedTheme =
    themePreference === "system" ? systemTheme : themePreference;

  useEffect(() => {
    applyThemeToDom(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (typeof window === "undefined" || themePreference !== "system") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    setSystemTheme(getSystemTheme());

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [themePreference]);

  const setThemePreference = useCallback((preference) => {
    if (!THEME_PREFERENCES.includes(preference)) return;
    setThemePreferenceState(preference);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    if (resolvedTheme === "dark") {
      setThemePreference("light");
      return;
    }
    setThemePreference("dark");
  }, [resolvedTheme, setThemePreference]);

  const value = useMemo(
    () => ({
      theme: resolvedTheme,
      themePreference,
      setThemePreference,
      toggleTheme,
    }),
    [resolvedTheme, themePreference, setThemePreference, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
